import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  Check, 
  Sparkles, 
  CreditCard,
  Shield,
  Star,
  Users,
  Loader2,
  Crown,
  Zap,
  Lock,
  Globe,
  Building2,
  Code,
  ChevronRight,
  X,
  Heart,
  BadgeCheck
} from "lucide-react";

// Payment icons - Using official badge style
const VisaIcon = () => (
  <div className="h-12 w-20 bg-[#1A1F71] rounded-lg flex items-center justify-center">
    <span className="text-white font-bold text-xl tracking-wider italic">VISA</span>
  </div>
);

const MastercardIcon = () => (
  <div className="h-12 w-20 bg-zinc-800 rounded-lg flex items-center justify-center overflow-hidden">
    <div className="flex items-center -space-x-3">
      <div className="w-8 h-8 rounded-full bg-[#EB001B]" />
      <div className="w-8 h-8 rounded-full bg-[#F79E1B]" />
    </div>
  </div>
);

const ApplePayIcon = () => (
  <div className="h-12 w-24 bg-black rounded-lg flex items-center justify-center gap-1">
    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
    <span className="text-white font-medium text-sm">Pay</span>
  </div>
);

const GooglePayIcon = () => (
  <div className="h-12 w-24 bg-white rounded-lg flex items-center justify-center border border-gray-300">
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
    <span className="text-gray-700 font-medium text-sm ml-1">Pay</span>
  </div>
);

const PremiumPage = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setPlans(response.data.plans);
      setTotalUsers(response.data.total_users);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleSubscribe = useCallback(async (planId) => {
    if (!token) {
      toast.error("Connectez-vous pour souscrire");
      navigate("/auth");
      return;
    }

    setProcessingPlan(planId);
    
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "success") {
        toast.success(response.data.message);
        await refreshUser();
        navigate("/dashboard");
      } else if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Erreur lors du paiement");
    } finally {
      setProcessingPlan(null);
    }
  }, [token, navigate, refreshUser]);

  const planFeatures = {
    early_access: [
      "Accès complet à l'application",
      "Système de notation",
      "Croisements GPS",
      "Crush & Match",
      "Badge Early Bird exclusif",
      "Gratuit à vie"
    ],
    premium: [
      "Tout Early Access +",
      "Mode invisible",
      "Statistiques avancées",
      "Filtres de recherche premium",
      "Support prioritaire",
      "Badge Premium"
    ],
    b2b_entreprise: [
      "Tout Premium +",
      "Dashboard entreprise",
      "Gestion multi-utilisateurs",
      "API limitée",
      "Support dédié",
      "Facturation entreprise"
    ],
    b2g_municipalite: [
      "Tout Entreprise +",
      "Dashboard collectivité",
      "Statistiques territoriales",
      "Intégration services publics",
      "Formation incluse",
      "SLA garanti"
    ],
    api_professional: [
      "Accès API complet",
      "1M requêtes/mois",
      "Webhooks",
      "Documentation technique",
      "Support développeur",
      "Sandbox inclus"
    ]
  };

  const planIcons = {
    early_access: Star,
    premium: Crown,
    b2b_entreprise: Building2,
    b2g_municipalite: Globe,
    api_professional: Code
  };

  const planColors = {
    early_access: "from-amber-500 to-orange-600",
    premium: "from-pink-500 to-rose-600",
    b2b_entreprise: "from-blue-500 to-indigo-600",
    b2g_municipalite: "from-emerald-500 to-teal-600",
    api_professional: "from-violet-500 to-purple-600"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/5">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="/logo-header.png" alt="RateShare" className="w-10 h-10" />
            <span className="text-xl font-bold">RATE<span className="text-amber-400">⭐</span>SHARE</span>
          </a>
          <div className="flex items-center gap-4">
            {user ? (
              <button 
                onClick={() => navigate("/dashboard")}
                className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                Mon compte
              </button>
            ) : (
              <button 
                onClick={() => navigate("/auth")}
                className="px-6 py-2 rounded-full bg-pink-500 hover:bg-pink-600 transition-colors font-medium"
              >
                Connexion
              </button>
            )}
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-400 text-sm font-medium mb-6">
              <Sparkles size={16} />
              {2026 - totalUsers} places Early Access restantes
            </span>
            
            <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-6">
              Passez à <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">Premium</span>
            </h1>
            
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-8">
              Débloquez toutes les fonctionnalités et rejoignez la communauté des utilisateurs engagés.
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-6 mb-12">
              <div className="flex items-center gap-2 text-zinc-400">
                <Shield size={20} className="text-green-500" />
                <span className="text-sm">Paiement sécurisé</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <Lock size={20} className="text-green-500" />
                <span className="text-sm">3D Secure</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-400">
                <BadgeCheck size={20} className="text-green-500" />
                <span className="text-sm">Conforme RGPD</span>
              </div>
            </div>

            {/* Payment methods */}
            <div className="flex justify-center items-center gap-4 mb-12">
              <VisaIcon />
              <MastercardIcon />
              <ApplePayIcon />
              <GooglePayIcon />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Main Plans - Early Access & Premium */}
          <div className="grid md:grid-cols-2 gap-8 mb-16 max-w-4xl mx-auto">
            {plans.filter(p => ["early_access", "premium"].includes(p.id)).map((plan, index) => {
              const Icon = planIcons[plan.id] || Star;
              const features = planFeatures[plan.id] || [];
              const colorClass = planColors[plan.id] || "from-pink-500 to-rose-600";
              const isPremium = plan.id === "premium";
              
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative rounded-3xl overflow-hidden ${isPremium ? 'ring-2 ring-pink-500' : ''}`}
                >
                  {isPremium && (
                    <div className="absolute top-0 left-0 right-0 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-center text-sm font-bold">
                      RECOMMANDÉ
                    </div>
                  )}
                  
                  <div className={`bg-zinc-900/80 backdrop-blur-xl border border-white/10 p-8 h-full ${isPremium ? 'pt-14' : ''}`}>
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center mb-6`}>
                      <Icon size={28} className="text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                    
                    <div className="flex items-baseline gap-1 mb-4">
                      {plan.price === 0 ? (
                        <span className="text-4xl font-black">Gratuit</span>
                      ) : (
                        <>
                          <span className="text-4xl font-black">{plan.price}€</span>
                          <span className="text-zinc-500">/mois</span>
                        </>
                      )}
                    </div>
                    
                    {plan.id === "early_access" && plan.spots_left !== null && (
                      <div className="mb-4">
                        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
                            style={{ width: `${((2026 - plan.spots_left) / 2026) * 100}%` }}
                          />
                        </div>
                        <p className="text-sm text-zinc-500 mt-2">{plan.spots_left} places restantes sur 2 026</p>
                      </div>
                    )}
                    
                    <ul className="space-y-3 mb-8">
                      {features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-3 text-zinc-300">
                          <Check size={18} className="text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={!plan.available || processingPlan === plan.id}
                      className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                        !plan.available 
                          ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                          : isPremium
                            ? 'bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white'
                            : 'bg-white text-black hover:bg-zinc-200'
                      }`}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {processingPlan === plan.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : !plan.available ? (
                        "Complet"
                      ) : plan.price === 0 ? (
                        <>
                          <Zap size={20} />
                          Activer gratuitement
                        </>
                      ) : (
                        <>
                          <CreditCard size={20} />
                          Souscrire maintenant
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Professional Plans */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-center mb-4">Offres Professionnelles</h2>
            <p className="text-zinc-400 text-center mb-12 max-w-2xl mx-auto">
              Solutions adaptées aux entreprises, collectivités et développeurs.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {plans.filter(p => ["b2b_entreprise", "b2g_municipalite", "api_professional"].includes(p.id)).map((plan, index) => {
                const Icon = planIcons[plan.id] || Star;
                const features = planFeatures[plan.id] || [];
                const colorClass = planColors[plan.id] || "from-blue-500 to-indigo-600";
                
                return (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="bg-zinc-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center mb-4`}>
                      <Icon size={24} className="text-white" />
                    </div>
                    
                    <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                    
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-3xl font-black">{plan.price}€</span>
                      <span className="text-zinc-500">/mois</span>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {features.slice(0, 4).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-zinc-400">
                          <Check size={14} className="text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={processingPlan === plan.id}
                      className="w-full py-3 rounded-xl font-medium bg-white/10 hover:bg-white/20 transition-all flex items-center justify-center gap-2"
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {processingPlan === plan.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Contacter <ChevronRight size={16} />
                        </>
                      )}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 px-6 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <Shield size={48} className="text-green-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Paiement 100% Sécurisé</h2>
          <p className="text-zinc-400 mb-8">
            Vos données bancaires sont traitées exclusivement par Stripe, leader mondial du paiement en ligne.
            Aucune information de carte n'est stockée sur nos serveurs.
          </p>
          
          <div className="grid md:grid-cols-4 gap-6 text-sm">
            <div className="bg-zinc-900 rounded-xl p-4 border border-white/10">
              <Lock size={24} className="text-green-500 mx-auto mb-2" />
              <p className="font-medium">Chiffrement TLS 1.3</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 border border-white/10">
              <Shield size={24} className="text-green-500 mx-auto mb-2" />
              <p className="font-medium">PCI-DSS Compliant</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 border border-white/10">
              <BadgeCheck size={24} className="text-green-500 mx-auto mb-2" />
              <p className="font-medium">3D Secure / SCA</p>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 border border-white/10">
              <Globe size={24} className="text-green-500 mx-auto mb-2" />
              <p className="font-medium">Conforme RGPD</p>
            </div>
          </div>
          
          <div className="mt-8 flex justify-center gap-6">
            <VisaIcon />
            <MastercardIcon />
            <ApplePayIcon />
            <GooglePayIcon />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Questions fréquentes</h2>
          
          <div className="space-y-4">
            {[
              {
                q: "Comment puis-je résilier mon abonnement ?",
                a: "Vous pouvez résilier à tout moment depuis votre espace Mon Compte. La résiliation prend effet à la fin de la période en cours."
              },
              {
                q: "Quels moyens de paiement acceptez-vous ?",
                a: "Nous acceptons Visa, Mastercard, American Express, Apple Pay et Google Pay. Tous les paiements sont sécurisés par Stripe."
              },
              {
                q: "L'offre Early Access est-elle vraiment gratuite à vie ?",
                a: "Oui ! Les 2 026 premiers utilisateurs bénéficient d'un accès gratuit et permanent à toutes les fonctionnalités de base."
              },
              {
                q: "Mes données bancaires sont-elles sécurisées ?",
                a: "Absolument. Vos données bancaires sont traitées exclusivement par Stripe (certifié PCI-DSS). Nous ne stockons aucune information de carte."
              }
            ].map((item, i) => (
              <details key={i} className="group bg-zinc-900/50 border border-white/10 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <span className="font-medium">{item.q}</span>
                  <ChevronRight size={20} className="text-zinc-500 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 pb-6 text-zinc-400">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center text-zinc-500 text-sm">
          <p className="mb-4">
            Paiement sécurisé par <span className="text-white font-medium">Stripe</span> • 
            Données hébergées en <span className="text-white font-medium">France (OVH)</span>
          </p>
          <p>© 2026 SAS RATESHARE. Tous droits réservés.</p>
          <div className="flex justify-center gap-6 mt-4">
            <a href="/terms" className="hover:text-white transition-colors">CGU</a>
            <a href="/privacy" className="hover:text-white transition-colors">Confidentialité</a>
            <a href="/legal" className="hover:text-white transition-colors">Mentions légales</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PremiumPage;
